import { Text } from '@mantine/core';
import { useNavigate } from 'react-router';

import { brand } from '../../branding';
import { useCatalogLabels } from '../labels';
import { WishlistIconCompact } from './HeaderWishlist';
import { LanguageSelectCompact } from './LanguageSwitch';

// Secondary bar shown only below `md`, directly under the yellow DOKA header bar. That bar comes
// from the private @uag/react-core BaseLayout blueprint - there's no prop to inject content into
// it - so HeaderBrand/HeaderWishlist/LanguageSwitch fake their placement with `position: fixed`
// overlays sized/offset for desktop widths. On narrow screens the yellow bar itself shrinks
// (smaller logo) while those overlays don't, so title/wishlist/language select collide (see chat
// screenshots). Those three now hide themselves below `md`, and this renders the same three
// affordances in normal document flow instead - nothing left fighting over space in the yellow
// bar itself.
//
// Colours here stay DOKA blue regardless of the configured brand (unlike the overlays, which follow
// brand.headerForeground): this bar has a white background of its own, so a brand's light-on-dark
// header foreground would be invisible in it.
//
// `showTitle` mirrors whether the calling page also renders HeaderBrand on desktop - only
// CatalogPage and LandingPage do (the title overlay was never added to the detail/viewer/wishlist
// pages), so this defaults to false to match that existing behaviour rather than introducing the
// title somewhere it didn't show before.
//
// Total footprint (content row height + bottom margin) this bar occupies in normal document flow on
// mobile. It used to be subtracted by the two full-page PDF viewers from their own `100vh - Npx`
// height; those are gone (the viewer is an overlay now, see DocumentOverlayPage), so this is only
// exported for any future caller that has to reserve the same space. ROW_HEIGHT is this constant
// minus the mb-4 margin below; keep both in sync with the className.
export const MOBILE_HEADER_BAR_HEIGHT = 56;
const ROW_HEIGHT = MOBILE_HEADER_BAR_HEIGHT - 16;

export const MobileHeaderBar = ({ showTitle = false }: { showTitle?: boolean }) => {
    const { L } = useCatalogLabels();
    const navigate = useNavigate();

    return (
        <div
            className="mb-4 flex items-center justify-between gap-3 bg-white px-1 md:hidden"
            // Page content, not layout chrome: FitToViewport looks for the blueprint's header as a
            // wide block above the page and must not take this bar for it.
            data-pih-page=""
            style={{ height: ROW_HEIGHT, borderBottom: '1px solid var(--pih-border)' }}
        >
            {showTitle ? (
                <Text c={brand.primary} fw={700} size="sm" style={{ cursor: 'pointer' }} truncate onClick={() => navigate('/')}>
                    {L.title}
                </Text>
            ) : (
                <span />
            )}
            <div className="flex shrink-0 items-center gap-3">
                <WishlistIconCompact />
                <LanguageSelectCompact />
            </div>
        </div>
    );
};
